import React, { useState, useEffect } from 'react';

function AboutUsEditor({ aboutUs, onUpdate, orgId, onSave, saving, savedMessage }) {
  const [form, setForm] = useState({
    mission: '', vision: '', description: '', yearFounded: '', registrationNumber: ''
  });

  useEffect(() => {
    if (aboutUs && typeof aboutUs === 'object') {
      setForm({
        mission: aboutUs.mission || '',
        vision: aboutUs.vision || '',
        description: aboutUs.description || '',
        yearFounded: aboutUs.yearFounded || '',
        registrationNumber: aboutUs.registrationNumber || ''
      });
    }
  }, [aboutUs]);

  const handleChange = (field, value) => {
    const updated = { ...form, [field]: value };
    setForm(updated);
    onUpdate(updated);
  };

  const descCount = form.description.length;
  const descClass = descCount > 1000 ? 'error' : descCount > 800 ? 'warning' : '';

  return (
    <div className="bb-editor">
      <div className="bb-editor-header">
        <div>
          <h2>About Us</h2>
          <p className="bb-editor-desc">Share your organisation's story, mission and vision.</p>
        </div>
        <div className="bb-editor-actions">
          {savedMessage && <span className={`bb-save-msg ${savedMessage.includes('Error') ? 'error' : ''}`}>{savedMessage}</span>}
          <button className="bb-btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Organisation Description</h3>

        <div className="bb-form-group">
          <label htmlFor="mission">Mission Statement</label>
          <textarea
            id="mission" rows={3} className="bb-textarea"
            value={form.mission}
            onChange={(e) => handleChange('mission', e.target.value)}
            placeholder="What is your organisation's core mission? e.g. To support and empower widows in Singapore..."
          />
          <span className="bb-hint">Summarise your purpose in 1–2 sentences.</span>
        </div>

        <div className="bb-form-group">
          <label htmlFor="vision">Vision Statement</label>
          <textarea
            id="vision" rows={3} className="bb-textarea"
            value={form.vision}
            onChange={(e) => handleChange('vision', e.target.value)}
            placeholder="What future does your organisation work toward? e.g. A Singapore where no widow walks alone..."
          />
        </div>

        <div className="bb-form-group">
          <label htmlFor="description">Full Description <span className="bb-required">*</span></label>
          <textarea
            id="description" rows={10} className={`bb-textarea ${descClass}`}
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Tell your community your full story — history, team, values, what makes you unique..."
          />
          <div className="bb-char-count" data-status={descClass}>
            {descCount} characters {descCount > 1000 && '(recommended maximum: 1000)'}
          </div>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Organisation Details</h3>
        <div className="bb-form-row">
          <div className="bb-form-group">
            <label htmlFor="yearFounded">Year Founded</label>
            <input
              id="yearFounded" type="text" className="bb-input"
              value={form.yearFounded}
              onChange={(e) => handleChange('yearFounded', e.target.value)}
              placeholder="e.g. 2003"
              maxLength={4}
            />
          </div>
          <div className="bb-form-group">
            <label htmlFor="registrationNumber">Charity / Registration Number</label>
            <input
              id="registrationNumber" type="text" className="bb-input"
              value={form.registrationNumber}
              onChange={(e) => handleChange('registrationNumber', e.target.value)}
              placeholder="e.g. ROS-1234/2003"
            />
          </div>
        </div>
      </div>

      <div className="bb-form-section">
        <h3 className="bb-section-title">Preview</h3>
        <div className="bb-preview-card">
          {form.mission && (
            <div className="bb-preview-block">
              <strong>Mission</strong>
              <p>{form.mission}</p>
            </div>
          )}
          {form.vision && (
            <div className="bb-preview-block">
              <strong>Vision</strong>
              <p>{form.vision}</p>
            </div>
          )}
          {form.description && (
            <div className="bb-preview-block">
              <strong>About Us</strong>
              <p style={{ whiteSpace: 'pre-wrap' }}>{form.description}</p>
            </div>
          )}
          {!form.mission && !form.vision && !form.description && (
            <p className="bb-preview-empty">Your About Us content will appear here once you start typing.</p>
          )}
        </div>
      </div>

      <div className="bb-tips-box">
        <h4>Writing Tips</h4>
        <ul>
          <li>Share your origin story — why was the organisation founded?</li>
          <li>Highlight who you serve and how you make a difference</li>
          <li>Mention any awards, recognition, or partnerships</li>
          <li>Use a warm, inclusive tone — your members are your community</li>
          <li>Aim for 200–500 words in the full description</li>
        </ul>
      </div>
    </div>
  );
}

export default AboutUsEditor;
